import get from 'lodash/get';
import SendBirdSDK from 'sendbird';
import { actions, mutations } from './chat.d';
import { formatData } from './chat.transform';
import Config from '@/config';

/**
 * Initialize Sendbird SDK
 * Our chat whitelabel API
 */
const SDK = new SendBirdSDK({ appId: Config.SendbirdAppId });
const ChannelHandler = new SDK.ChannelHandler();

/**
 * Singleton instance of active channel that
 * being used mainly to interact with
 */
let CurrentChannel = null;

export default {
  /**
   * Connect to the chat service
   * @param {Object} user user credential
   */
  [actions.connect](context, user) {
    return new Promise((resolve, reject) => {
      SDK.connect(user.id, user.token, (user, error) => {
        if (error) reject(error);
        resolve(user);
      });
    });
  },

  /**
   * Disconnect from the chat service
   */
  [actions.disconnect]() {
    return new Promise((resolve, reject) => {
      SDK.disconnect(error => {
        if (error) reject(error);
        resolve();
      });
    });
  },

  /**
   * Open the chat's channel
   * @param {string} url the url of the channel
   */
  [actions.enterChannel](context, url) {
    return new Promise((resolve, reject) => {
      SDK.GroupChannel.getChannel(url, (groupChannel, error) => {
        if (error) reject(error);
        groupChannel.join((response, error) => {
          if (error) reject(error);

          CurrentChannel = groupChannel;
          resolve(response);
        });
      });
    });
  },

  /**
   * Close the chat's channel
   */
  [actions.leaveChannel]() {
    return new Promise((resolve, reject) => {
      if (CurrentChannel) {
        CurrentChannel.leave((response, error) => {
          if (error) reject(error);
          resolve(response);
        });
      } else {
        resolve();
      }
    });
  },

  /**
   * Close the chat's channel
   */
  [actions.updateCustomType](context, payload) {
    return new Promise((resolve, reject) => {
      if (!CurrentChannel) {
        reject('Channel is not initialized');
      }

      let params = new SDK.GroupChannelParams();
      params.customType = payload.customType;

      CurrentChannel.updateChannel(params, (groupChannel, error) => {
        if (error) {
          reject('Failed to update channel custom type.');
        } else {
          resolve(groupChannel);
        }
      });
    });
  },

  /**
   * Used to force concierge
   * to leave all previous channels
   */
  [actions.leavePreviousChannels]() {
    return new Promise((resolve, reject) => {
      const channelListQuery = SDK.GroupChannel.createMyGroupChannelListQuery();
      channelListQuery.includeEmpty = true;
      channelListQuery.limit = 100; // Maximum limit

      if (channelListQuery.hasNext) {
        channelListQuery.next(function(channelList, error) {
          if (error) reject(error);

          const leaveChannels = channelList.map(
            channel =>
              new Promise((resolve2, reject2) => {
                channel.leave((response, error2) => {
                  if (error) reject2(error2);
                  resolve2(response);
                });
              })
          );

          // Leave all channels at once
          Promise.all(leaveChannels)
            .then(resolve)
            .catch(reject);
        });
      }
    });
  },

  /**
   * Retrieve the initial  batch of the messages
   */
  [actions.loadInitialMessages](context) {
    return new Promise((resolve, reject) => {
      if (!CurrentChannel) {
        reject('Channel is not initialized');
      }

      const prevMessageListQuery = CurrentChannel.createPreviousMessageListQuery();
      prevMessageListQuery.limit = 15;
      prevMessageListQuery.reverse = false;

      prevMessageListQuery.load((messages, error) => {
        if (error) reject(error);
        context.commit(mutations.setMessages, messages);
        resolve();
      });
    });
  },

  /**
   * Retrieve the older messages than in previous
   */
  [actions.loadOlderMessages](context) {
    return new Promise((resolve, reject) => {
      if (!CurrentChannel) {
        reject('Channel is not initialized');
      }

      const limit = 30;
      const callback = (olderMessages, error) => {
        if (error) reject(new Error(error.message));
        const clippedMessages = olderMessages.slice(0, -1);
        context.commit(mutations.insertOlderMessages, clippedMessages);
        const canLoadMore = limit - clippedMessages.length < 2;
        resolve(canLoadMore);
      };

      const { messages } = context.state;
      const lastMessageId = messages[0].messageId;
      const params = [lastMessageId, true, limit, false, '', '', callback];

      // msgId, isDistinct, limit, isReverse, messageType, customType, callback
      CurrentChannel.getPreviousMessagesByID(...params);
    });
  },

  /**
   * Send simple text chat message
   * @param {String} message the text which will be sent
   */
  [actions.sendTextMessage](context, message) {
    return new Promise((resolve, reject) => {
      if (!CurrentChannel) {
        reject('Channel is not initialized');
      }

      const callback = (message, error) => {
        if (error) reject(error);
        context.commit(mutations.resolveNewMessage, message);
        resolve();
      };

      const { currentConcierge } = context.rootState.Concierge;
      const { currentUser } = context.rootState.User;
      const data = formatData({
        senderId: get(currentConcierge, 'agentId'),
        senderName: get(currentConcierge, 'screenName'),
        receiverId: get(currentUser, 'userId'),
        language: get(currentUser, 'language')
      });

      const params = [message, data, 'text', callback];
      const tempMessage = CurrentChannel.sendUserMessage(...params);
      context.commit(mutations.insertNewMessage, tempMessage);
    });
  },

  [actions.deleteMessage](context, payload) {
    return new Promise((resolve, reject) => {
      if (!CurrentChannel) {
        reject('Channel is not initialized');
      }
      const callback = (_, error) => {
        if (error) reject(error);
        resolve();
      };

      const message = { messageId: payload };
      CurrentChannel.deleteMessage(message, callback);
    });
  },

  /**
   * Send image message with defined url
   * @param {Object} payload the data of the image
   */
  [actions.sendImage](context, payload) {
    return new Promise((resolve, reject) => {
      if (!CurrentChannel) {
        reject('Channel is not initialized');
      }

      const callback = (message, error) => {
        if (error) reject(error);
        context.commit(mutations.resolveNewMessage, message);
        resolve();
      };

      const { currentConcierge } = context.rootState.Concierge;
      const { currentUser } = context.rootState.User;
      const data = formatData({
        senderId: get(currentConcierge, 'agentId'),
        senderName: get(currentConcierge, 'screenName'),
        receiverId: get(currentUser, 'userId'),
        language: get(currentUser, 'language')
      });
      const { url, name, type, size } = payload;
      const params = [url, name, type, size, data, 'image', callback];
      const tempMessage = CurrentChannel.sendFileMessage(...params);
      context.commit(mutations.insertNewMessage, tempMessage);
    });
  },

  [actions.startTyping]() {
    if (!CurrentChannel) {
      throw new Error('Channel is not initialized');
    }

    CurrentChannel.startTyping();
  },

  [actions.endTyping]() {
    if (!CurrentChannel) {
      throw new Error('Channel is not initialized');
    }

    CurrentChannel.endTyping();
  },

  /**
   * Handler when message is received
   * save it to the local store
   */
  handleOnMessageReceived(context, message) {
    context.commit(mutations.insertNewMessage, message);
  },

  /**
   * Where the listener invoked
   */
  [actions.registerChatListener](context) {
    const { listenerHandlerId } = context.state;

    // Listener when message is receieved
    ChannelHandler.onMessageReceived = function({ url }, message) {
      // If incoming message coming from other than current channel, scrap it
      const { currentChannel } = context.rootState.Channel;
      if (url !== currentChannel.channelUrl) return;

      context.dispatch('handleOnMessageReceived', message);
    };

    SDK.addChannelHandler(listenerHandlerId, ChannelHandler);
  },

  [actions.unregisterChatListener](context) {
    const { listenerHandlerId } = context.state;
    SDK.removeChannelHandler(listenerHandlerId);
  },

  /**
   * Used to update chat profile photo and nick name
   */
  [actions.updateUserInfo](context, { screenName, profileUrl }) {
    return new Promise((resolve, reject) => {
      SDK.updateCurrentUserInfo(screenName, profileUrl, (result, error) => {
        if (error) reject(error);
        resolve();
      });
    });
  }
};
